import { useCallback, useEffect, useRef } from 'react'
import type { Agent, AgentPlan, Claim, Message, MessageReactions, RoomFrame, RoomStatus } from '../api/types'

/**
 * How long the room may hear nothing at all before its stream is presumed dead.
 *
 * The server sends a `ping` every twenty seconds precisely so a client can
 * answer this question, so anything past two of those windows is a stream that
 * is no longer attached to a server. Generous rather than tight: a reconnect
 * costs a request, and a room that is merely quiet must not churn through
 * connections all afternoon.
 */
const SILENCE_LIMIT = 50_000

/** How often the watchdog looks. Cheap, and bounds how late a reconnect is. */
const WATCH_EVERY = 5_000

/**
 * How long to wait before each attempt at reopening a stream, or at loading a
 * room's first page again, and the last value repeated forever after. A deploy
 * is back inside the first few; a box that is genuinely down should not be
 * asked once a second for an hour.
 */
export const BACKOFF = [1_000, 10_000, 30_000] as const

/** The frame names listened for. A name a newer server adds reaches nothing here. */
const FRAMES: readonly RoomFrame['name'][] = [
  'message', 'reactions', 'edited', 'presence', 'claims', 'status', 'plans', 'lessons', 'synced',
]

/** What a room does with the snapshot frames. Each is optional. */
export interface RoomFrameHandlers {
  reactions?: (frame: MessageReactions) => void
  /** A message its sender edited, whole. It moves no cursor. */
  edited?: (message: Message) => void
  presence?: (roster: Agent[]) => void
  claims?: (claims: Claim[]) => void
  status?: (status: RoomStatus) => void
  /** Every running agent's plan, whole, each time any of them changes. */
  plans?: (plans: AgentPlan[]) => void
  /**
   * A connection's catch-up is over. `named` is every message a `reactions`
   * frame named since it opened: a held message missing from it has none left.
   */
  synced?: (named: ReadonlySet<string>) => void
}

/** Attach a listener to every frame a room's stream delivers; call what it returns to detach. */
export type OnRoomFrame = (listener: (frame: RoomFrame) => void) => () => void

/**
 * Subscribe to a room's event stream, and keep it subscribed.
 *
 * Owning the reconnection rather than leaving it to `EventSource` is the whole
 * point of this module. The browser's own retry covers exactly one failure
 * mode, a connection that visibly breaks, and the two that actually happen
 * here are not it.
 *
 * A stream can die without the browser noticing. A proxy between the tab and
 * the server, or a laptop that slept, leaves a socket that reports
 * `readyState === OPEN` and carries nothing ever again; no `error` fires, so
 * the built-in retry never runs and the room silently stops updating until
 * somebody reloads the page. The keepalive is the only evidence available, so
 * silence past `SILENCE_LIMIT` is treated as death and the stream is replaced.
 *
 * And a stream that fails at the HTTP level, a 502 while the API restarts,
 * is fatal to `EventSource`: it closes for good rather than retrying, so a
 * deploy would leave every open tab dead. Every failure here is retried with
 * backoff instead.
 *
 * Every connection carries a cursor, so the server replays the gap rather
 * than the whole room, which it sends from the first message on, 200 a second,
 * when asked for everything. The first connection starts from `since`, so a
 * room passes `channelId` only once its first page has loaded, with `since`
 * the id of the last message on it, and null while it loads. After that the
 * cursor is kept in a ref and moved forward as messages land, and a reconnect
 * resumes from wherever it has got to.
 *
 * This is what makes the product claim true: a CLI `say` appears in an open
 * browser without a refresh, and goes on doing so after a deploy, a sleep and
 * a lost network.
 *
 * Returns how anything else hears the same frames without a second stream.
 */
export function useLiveMessages(
  channelId: string | null,
  since: string | null,
  onMessage: (message: Message) => void,
  handlers: RoomFrameHandlers = {},
): OnRoomFrame {
  // Read by the stream, which must not be torn down and rebuilt every time the
  // room re-renders: a new subscription per arrival is the loop this avoids.
  const onMessageRef = useRef(onMessage)
  onMessageRef.current = onMessage
  const handlersRef = useRef(handlers)
  handlersRef.current = handlers
  const listeners = useRef(new Set<(frame: RoomFrame) => void>())

  // Where to resume from. Fed by both the page load, through `since`, and by
  // what the stream itself delivers, and only ever moves forward. Ids sort
  // lexicographically, so this is a string comparison.
  const cursor = useRef<string | null>(null)

  // Which room that cursor belongs to. The render that moves to another room
  // happens before the subscription below moves, and until then the last
  // room's stream is still open on this cursor. Ids carry the wall clock, so a
  // cursor from one room asks another for messages newer than everything in
  // it. Only a `since` from a render after the subscription moved is taken
  // here; the one it moved with is taken there.
  const subscribed = useRef<string | null>(null)
  if (subscribed.current === channelId && since && (!cursor.current || since > cursor.current)) {
    cursor.current = since
  }
  // Per connection: what its opening snapshot named, and whether it is still arriving.
  const named = useRef(new Set<string>())
  const syncing = useRef(true)

  useEffect(() => {
    if (!channelId) return
    // A different room resumes from its own page, not from the last one's.
    cursor.current = since
    subscribed.current = channelId
    // `since` is read once here and on every render above rather than depended
    // on: re-subscribing on every arrival would reconnect constantly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId])

  useEventStream(channelId && `/v1/channels/${channelId}/events`, FRAMES, {
    query: () => (cursor.current ? `?since=${encodeURIComponent(cursor.current)}` : ''),
    onConnect: () => {
      named.current = new Set()
      syncing.current = true
    },
    deliver: (raw) => {
      const frame = raw as RoomFrame
      const handle = handlersRef.current
      switch (frame.name) {
        case 'message':
          if (!cursor.current || frame.data.id > cursor.current) cursor.current = frame.data.id
          onMessageRef.current(frame.data)
          break
        case 'reactions':
          if (syncing.current) named.current.add(frame.data.message_id)
          handle.reactions?.(frame.data)
          break
        case 'edited': handle.edited?.(frame.data); break
        case 'presence': handle.presence?.(frame.data); break
        case 'claims': handle.claims?.(frame.data); break
        case 'status': handle.status?.(frame.data); break
        case 'plans': handle.plans?.(frame.data); break
        case 'synced':
          syncing.current = false
          handle.synced?.(named.current)
          break
      }
      for (const listener of listeners.current) listener(frame)
    },
  })

  return useCallback<OnRoomFrame>((listener) => {
    listeners.current.add(listener)
    return () => listeners.current.delete(listener)
  }, [])
}

/** One frame as it arrived: the event's name and its parsed data, for the owner to type. */
export interface StreamFrame {
  name: string
  data: unknown
}

/** How a stream's frames reach its owner, read afresh on every connection and frame. */
export interface StreamOptions {
  deliver: (frame: StreamFrame) => void
  /** Appended to the path on each connection, so a reconnect can resume. */
  query?: () => string
  onConnect?: () => void
}

/**
 * One EventSource on `path`, kept open by the reconnection, silence watch and
 * backoff `useLiveMessages` explains, for every stream the app holds. A null
 * path holds none; a new path closes the old stream and opens its own.
 */
export function useEventStream(
  path: string | null,
  frames: readonly string[],
  options: StreamOptions,
): void {
  const latest = useRef(options)
  latest.current = options

  useEffect(() => {
    if (!path) return

    let stopped = false
    let source: EventSource | null = null
    let attempt = 0
    let heardAt = Date.now()
    let pending: ReturnType<typeof setTimeout> | undefined

    const heard = () => {
      heardAt = Date.now()
    }

    const onOpen = () => {
      // Connected, so the next outage starts its backoff from the beginning.
      attempt = 0
      heard()
    }

    const onFrame = (event: Event) => {
      heard()
      let data: unknown
      try {
        data = JSON.parse((event as MessageEvent).data)
      } catch {
        // A frame we cannot parse is not worth tearing the stream down for.
        return
      }
      latest.current.deliver({ name: event.type, data })
    }

    const close = () => {
      if (!source) return
      source.removeEventListener('open', onOpen)
      for (const name of frames) source.removeEventListener(name, onFrame)
      source.removeEventListener('ping', heard)
      source.removeEventListener('error', onError)
      source.close()
      source = null
    }

    const connect = () => {
      if (stopped) return
      close()
      const opened = new EventSource(`${path}${latest.current.query?.() ?? ''}`)
      opened.addEventListener('open', onOpen)
      for (const name of frames) opened.addEventListener(name, onFrame)
      opened.addEventListener('ping', heard)
      opened.addEventListener('error', onError)
      source = opened
      latest.current.onConnect?.()
      heard()
    }

    /** Queue another attempt, unless one is already waiting. */
    const retryIn = (delay: number) => {
      if (stopped || pending !== undefined) return
      close()
      pending = setTimeout(() => {
        pending = undefined
        connect()
      }, delay)
    }

    /**
     * Open another stream at once, dropping whatever retry was queued.
     *
     * For the two cases where waiting is the wrong answer: a tab somebody is
     * looking at again, and a network that has just come back. Both say more
     * about whether a connection will succeed than the backoff does, and both
     * are rare enough that skipping it costs nothing.
     */
    const reconnectNow = () => {
      if (stopped) return
      if (pending !== undefined) {
        clearTimeout(pending)
        pending = undefined
      }
      connect()
    }

    function onError() {
      retryIn(BACKOFF[Math.min(attempt++, BACKOFF.length - 1)])
    }

    const silent = () => Date.now() - heardAt > SILENCE_LIMIT

    const watchdog = setInterval(() => {
      // A queued retry is already handling it, and cutting in front of the
      // backoff is how a server that is down gets asked once every five
      // seconds for as long as it stays down.
      if (pending === undefined && silent()) reconnectNow()
    }, WATCH_EVERY)

    /**
     * A tab that was hidden gets asked the moment it is looked at, rather than
     * waiting out the watchdog. Background timers are throttled, so the
     * interval above may not have run for as long as the tab was away.
     */
    const wake = () => {
      if (document.visibilityState !== 'hidden' && silent()) reconnectNow()
    }

    document.addEventListener('visibilitychange', wake)
    window.addEventListener('online', reconnectNow)
    connect()

    return () => {
      stopped = true
      document.removeEventListener('visibilitychange', wake)
      window.removeEventListener('online', reconnectNow)
      clearInterval(watchdog)
      if (pending !== undefined) clearTimeout(pending)
      close()
    }
    // Every caller passes `frames` as a module constant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path])
}
